import React, { useState } from 'react';
import { Film, ArrowRight } from 'lucide-react';

/**
 * "What's new" — Additional Cuts, EDITOR variant.
 *
 * THE FIRST JOB OF THIS ANNOUNCEMENT IS TELLING MOST READERS THEY CAN IGNORE IT.
 * Cuts are rare (a handful of projects a month across all editors), and the
 * failure mode worth designing against is an editor deciding "cut" means
 * "revision" and burning uploads on it. Every screen says who does NOT need
 * this before it says anything else.
 *
 * ILLUSTRATIONS, NOT SCREENSHOTS — drawn in markup so they follow the theme and
 * cannot go stale (same decision as FileRequestsAnnouncement). The pill mock
 * mirrors the real cut-switcher classes; the labels shown ("Horizontal",
 * "Vertical") are examples of USER-TYPED text, not product strings, so there is
 * nothing to import.
 */

const Eyebrow = ({ children }) => (
  <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 block">
    {children}
  </span>
);

const Numbered = ({ rows }) => (
  <div className="p-6 py-5 space-y-2.5 border-t border-ug-border-subtle">
    {rows.map(([t, d], i) => (
      <div key={i} className="flex gap-3">
        <span className="shrink-0 w-5 h-5 rounded-full bg-ug-gold/15 border border-ug-gold/25 text-ug-gold font-sans text-[10px] font-semibold flex items-center justify-center">{i + 1}</span>
        <p className="text-[12px] text-zinc-100/75 leading-relaxed">
          <span className="text-zinc-100 font-medium">{t}.</span> {d}
        </p>
      </div>
    ))}
  </div>
);

/* One version, two labelled files — what the client ends up seeing. */
const SwitcherIllustration = () => (
  <div className="p-3 rounded-xl bg-ug-surface/50 border border-ug-border">
    <Eyebrow>One version, every file of the same edit</Eyebrow>
    <div className="mt-2 flex items-center gap-1.5">
      <span className="px-2.5 py-1 rounded-full bg-ug-surface border border-ug-border text-[10px] font-sans font-semibold text-zinc-100/80">V1</span>
      <span className="px-2.5 py-1 rounded-full bg-gold-leaf text-obsidian text-[9px] font-sans font-semibold uppercase tracking-wider">Horizontal</span>
      <span className="px-2.5 py-1 rounded-full bg-ug-surface border border-ug-border text-[9px] font-sans font-semibold uppercase tracking-wider text-zinc-100/70">Vertical</span>
    </div>
    <div className="mt-2.5 space-y-1.5">
      {[['wedding_film.mp4', 'Horizontal'], ['wedding_film_9x16.mp4', 'Vertical']].map(([f, l]) => (
        <div key={f} className="flex items-center gap-2 rounded bg-ug-surface border border-ug-border-strong px-2.5 py-1.5">
          <Film className="w-3 h-3 text-ug-gold/50 shrink-0" />
          <span className="text-[10px] text-zinc-100/75 truncate flex-1">{f}</span>
          <span className="text-[9px] font-sans font-semibold uppercase tracking-wider text-oxidized-teal shrink-0">{l}</span>
        </div>
      ))}
    </div>
  </div>
);

/* The two V2+ controls the rules step talks about. Example labels, user-typed. */
const RulesIllustration = () => (
  <div className="p-3 rounded-xl bg-ug-surface/50 border border-ug-border space-y-2">
    <Eyebrow>On your next version</Eyebrow>
    <div className="flex items-center gap-2 rounded bg-ug-surface border border-ug-border-strong px-2.5 py-2">
      <span className="w-3 h-3 rounded-sm border border-ug-border-strong bg-gold-leaf/80 shrink-0" />
      <span className="text-[10px] text-zinc-100/75">"Vertical" unchanged since V1</span>
    </div>
    <div className="flex items-center gap-2 rounded bg-ug-surface border border-ug-border-strong px-2.5 py-2">
      <span className="text-[10px] text-slate-500 shrink-0">or re-upload with the same label:</span>
      <span className="rounded border border-ug-border bg-ug-raised px-2 py-0.5 text-[10px] text-zinc-100/80">Vertical</span>
    </div>
    <p className="text-[10px] text-slate-500 italic">
      Until every earlier cut is confirmed one way or the other, the upload button stays off.
    </p>
  </div>
);

const steps = [
  {
    eyebrow: 'Most editors will never need this',
    title: 'Additional Cuts',
    blurb: 'Some clients order the same edit delivered as more than one file. When that happens, all the files now go up together on one version — each with a label.',
    body: <SwitcherIllustration />,
    rows: [
      ['A cut is the same edit, exported again', 'A vertical 9:16 copy of the highlight for social media, next to the horizontal one. Or the Ceremony and Reception delivered as two separate files instead of one long file. Same edit, different file.'],
      ['A cut is NOT a revision', 'If you fixed notes and re-exported, that is your next Version — upload it exactly like you always have. Additional Cuts stays empty.'],
      ['No cuts on the order? Nothing changes', 'The Review Video box works exactly as before. If your project is one video, you can close this and forget it.'],
    ],
  },
  {
    eyebrow: 'The rules, for when it does come up',
    title: 'Labels carry the cut between versions',
    blurb: 'Each file gets a short label you type — that label is how the system knows V2’s "Vertical" is the same cut as V1’s.',
    body: <RulesIllustration />,
    rows: [
      ['You name every file', 'e.g. "Horizontal", "Vertical", "Ceremony". Type the same label on every version of that cut — the label is its identity.'],
      ['No cut is left behind on V2+', 'Every cut the last version had must be re-uploaded (same label) or ticked "unchanged" — the big file never needs re-uploading if it didn’t change. The submit stays blocked until each one is handled.'],
      ['Client notes are tagged per cut', 'Still one notes list per round. A note the client left while watching the Vertical shows a Vertical tag, so you know which file they mean.'],
    ],
  },
];

export function CutsEditorAnnouncement({ onAcknowledge }) {
  const [step, setStep] = useState(0);
  const last = step === steps.length - 1;
  return (
    <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-ug-raised border border-ug-border shadow-2xl"
         data-testid="whats-new-cuts-editor">
      <div className="p-6 pb-5 border-b border-ug-border-subtle">
        <div className="flex items-center gap-2 mb-3">
          <span className="font-sans text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-ug-gold/15 text-ug-gold border border-ug-gold/25">New</span>
          <Film className="w-3.5 h-3.5 text-ug-gold" strokeWidth={2} />
          <span className="ml-auto font-sans text-[9px] uppercase tracking-wider text-slate-500">{steps[step].eyebrow}</span>
        </div>
        <h2 className="font-heading text-2xl text-zinc-100 mb-2">{steps[step].title}</h2>
        <p className="text-[13px] text-zinc-100/75 leading-relaxed">{steps[step].blurb}</p>
      </div>
      <div className="p-6 py-5 bg-ug-surface/30">{steps[step].body}</div>
      <Numbered rows={steps[step].rows} />
      <div className="p-6 pt-5 border-t border-ug-border-subtle bg-ug-surface/20 flex items-center gap-3">
        <div className="flex gap-1.5">
          {steps.map((_, n) => (
            <span key={n} className={`w-1.5 h-1.5 rounded-full ${n === step ? 'bg-ug-gold' : 'bg-ug-border-strong'}`} />
          ))}
        </div>
        <button
          data-testid="whats-new-next"
          onClick={() => (last ? onAcknowledge && onAcknowledge() : setStep(n => n + 1))}
          className="tap-auto ml-auto px-5 py-2.5 rounded-lg bg-gold-leaf text-obsidian font-sans text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1.5">
          {last ? 'Got it' : 'Next'}
          {!last && <ArrowRight className="w-3 h-3" />}
        </button>
      </div>
    </div>
  );
}

export default CutsEditorAnnouncement;
